import { FileWithPath, MIME_TYPES } from '@mantine/dropzone'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { uniqueId } from 'lodash'

import { WizardHeader } from '../../../../../common/components/wizardHeader/WizardHeader'

import styles from './BulkWalletWizard.module.scss'
import {
  AcknowledgeModal,
  ActionModal,
  Alert,
  Dropzone,
  FullPageFormLayout,
  Heading,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableProvider,
  TableRow,
  Typography
} from '@percent/lemonade'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { useQuery, useMutation } from '@percent/partner-dashboard/common/hooks'
import { APIError } from '@percent/partner-dashboard/common/hooks/useMutation/useMutation.types'
import { formatMoney } from '@percent/utility'

export enum BulkWalletErrorCode {
  InvalidRecords = 'donation_wallet/add_wallet_balances/invalid_records',
  MissingHeaders = 'donation_wallet/add_wallet_balances/missing_required_csv_headers',
  InvalidCsv = 'donation_wallet/add_wallet_balances/invalid_csv_file',
  EmptyCsv = 'donation_wallet/add_wallet_balances/empty_csv_file',
  CsvRowsExceeded = 'donation_wallet/add_wallet_balances/max_csv_rows_exceeded'
}
export function BulkWalletWizard() {
  const [file, setFile] = useState<FileWithPath[]>([])
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const navigate = useNavigate()
  const {
    t,
    i18n: { language: locale }
  } = useTranslation()
  const [modalState, setModalState] = useState({
    isSuccess: false,
    isError: false
  })
  const { walletBalanceService } = useServices()
  const [fileErrorCode, setFileErrorCode] = useState<string | null>(null)

  const [{ body: fundingSourcesData, isLoading: isFundingSourcesLoading }] = useQuery(
    walletBalanceService.getFundingSources,
    {}
  )

  const hasFundingSources =
    !isFundingSourcesLoading && fundingSourcesData.data.filter(source => !source.disabledAt).length > 0

  const handleCloseSuccessModal = () => {
    setModalState(prev => ({ ...prev, isSuccess: false }))
    navigate('/workplace-giving/employees')
  }

  const handleCloseErrorModal = () => {
    setModalState(prev => ({ ...prev, isError: false }))
  }

  const columns = [
    { id: 'Row', props: { width: '10%' } },
    { id: 'Field', props: { width: '20%' } },
    { id: 'Field value', props: { width: '30%' } },
    { id: 'Error message', props: { width: '40%' } }
  ]

  const handleFileError = (apiError: APIError) => {
    const errorCode = apiError?.response?.data?.error?.code

    if (Object.values(BulkWalletErrorCode).includes(errorCode as BulkWalletErrorCode)) {
      setFileErrorCode(errorCode as BulkWalletErrorCode)
    } else {
      setModalState(prev => ({ ...prev, isError: true }))
    }
    setIsConfirmModalOpen(false)
  }

  const [{ data: previewData, isLoading: isPreviewLoading, error: previewError }, { apiFunc: previewFunc }] =
    useMutation(
      (csvFile: File) => walletBalanceService.addBatchWalletBalance(csvFile, true),
      () => {
        setFileErrorCode(null)
        setIsConfirmModalOpen(true)
      },
      handleFileError
    )

  const [{ data: applyData, isLoading: isApplyLoading, error: applyError }, { apiFunc: addBatchWalletBalanceFunc }] =
    useMutation(
      walletBalanceService.addBatchWalletBalance,
      () => {
        setIsConfirmModalOpen(false)
        setModalState(prev => ({ ...prev, isSuccess: true }))
      },
      handleFileError
    )

  const handleReviewUpload = () => {
    previewFunc(file[0])
  }

  const handleFileChange = (newFile: FileWithPath[]) => {
    setFile(newFile)
    setFileErrorCode(null)

    if (newFile.length) {
      previewFunc(newFile[0])
    }
  }

  const handleBatchWalletBalance = () => {
    addBatchWalletBalanceFunc(file[0])
  }

  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false)
  }

  const summary = previewData?.data?.data

  const currencyBreakdown = summary
    ? Object.entries(summary.amounts)
        .sort(([currencyA], [currencyB]) => currencyA.localeCompare(currencyB))
        .map(([currency, money]) => ({ currency, formattedAmount: formatMoney(money, { locale }) }))
    : []

  const error = previewError || applyError
  const isFileErrorInvalidRecords = fileErrorCode === BulkWalletErrorCode.InvalidRecords
  const isFileError = fileErrorCode && fileErrorCode !== BulkWalletErrorCode.InvalidRecords

  const totalErrors = error?.rowErrors?.length
  const uniqueRows = new Set(error?.rowErrors?.map(rowError => rowError.row)).size

  return (
    <div className={styles.bulkWalletWrapper}>
      <FullPageFormLayout
        actionsHeader={
          <WizardHeader
            title={t('typography.bulkWalletTopup')}
            onCancel={() => navigate('/workplace-giving/employees')}
            onPublish={handleReviewUpload}
            isPublishDisabled={!!fileErrorCode || !file.length || isPreviewLoading}
            isLoading={isPreviewLoading}
            publishBtnCopy={t('button.next')}
            toolTipCopy={fileErrorCode ? t('toolTip.reUploadCsv') : t('toolTip.addFunds')}
            publishBtnTestId="add-funds-btn"
            cancelBtnTestId="cancel-button"
          />
        }
      >
        <div className={styles.bulkWalletContent}>
          <div>
            <Heading level="h2">{t('typography.uploadFile')}</Heading>
            <p className={styles.uploadDescription}>
              <Trans
                i18nKey="typography.uploadDescription"
                components={{
                  a: (
                    <a
                      href="/assets/bulk_wallet_template.csv"
                      download="bulk_wallet_template.csv"
                      className={styles.uploadlink}
                    >
                      {t('typography.uploadDescription')}
                    </a>
                  )
                }}
              />
            </p>
            <ul className={styles.uploadList}>
              <li>
                <Trans i18nKey="typography.employeesEmailAdress" components={{ b: <strong /> }} />
              </li>
              <li>
                <Trans i18nKey="typography.addWalletBalance" components={{ b: <strong /> }} />
              </li>
              <li>
                <Trans i18nKey="typography.localCurrency" components={{ b: <strong /> }} />
              </li>
              <li>
                <Trans i18nKey="typography.expiresOn" components={{ b: <strong /> }} />
              </li>
              {hasFundingSources && (
                <li>
                  <Trans i18nKey="typography.sourceOfFunding" components={{ b: <strong /> }} />
                </li>
              )}
            </ul>
          </div>
          <Dropzone
            files={file}
            onChange={handleFileChange}
            maxFiles={1}
            maxSize={5 * 1024 * 1024}
            accept={[MIME_TYPES.csv]}
            title={t('typography.selectCsvFile')}
            description={t('typography.uploadCsvFile')}
            testId="bulk-wallet-upload"
          />
          {isFileErrorInvalidRecords || isFileError ? (
            <Alert
              variant="error"
              title={
                isFileError
                  ? t('errorMessage.bulkWalletTopup.invalidCsvFile')
                  : t('errorMessage.bulkWalletTopup.invalidRows', {
                      count: totalErrors,
                      totalErrors,
                      uniqueRows
                    })
              }
            >
              {isFileError
                ? error?.reasons?.[0] || error?.message
                : t('errorMessage.bulkWalletTopup.reviewErrors', {
                    count: totalErrors
                  })}
            </Alert>
          ) : null}

          {isFileErrorInvalidRecords && (
            <div className={styles.bulkWalletTable} data-testid="bulk-wallet-table">
              <TableProvider
                columns={columns}
                isLoading={false}
                illustration="nothing-to-see"
                totalResults={null}
                emptyTableTitle=""
                emptyTableDescription=""
              >
                <Table>
                  <TableBody>
                    {error?.rowErrors?.map(rowError => (
                      <TableRow key={uniqueId()}>
                        <TableCell>{rowError.row}</TableCell>
                        <TableCell>{rowError.field}</TableCell>
                        <TableCell>{rowError.fieldValue}</TableCell>
                        <TableCell>{rowError.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableProvider>
            </div>
          )}
        </div>
      </FullPageFormLayout>

      {summary && (
        <Modal open={isConfirmModalOpen} onClose={handleCloseConfirmModal} aria-labelledby="add-bulk-wallet-confirm">
          <ActionModal
            title={t('dialog.employees.bulkWallet.confirm.title')}
            primaryButtonText={t('button.addFunds')}
            secondaryButtonText={t('button.cancel')}
            handleClose={handleCloseConfirmModal}
            handleSubmit={handleBatchWalletBalance}
            loading={isApplyLoading}
            primaryBtnTestId="confirm-continue-btn"
            secondaryBtnTestId="confirm-cancel-btn"
          >
            <div className={styles.confirmContent}>
              <Typography>
                {t('dialog.employees.bulkWallet.confirm.description', {
                  employeeCount: summary.uniqueAccounts.toLocaleString(locale)
                })}
              </Typography>
              <div className={styles.currencyBreakdown} data-testid="bulk-wallet-currency-breakdown">
                <Typography variant="default-medium">
                  {t('dialog.employees.bulkWallet.confirm.breakdownTitle')}
                </Typography>
                <ul className={styles.currencyBreakdownList}>
                  {currencyBreakdown.map(({ currency, formattedAmount }) => (
                    <li key={currency} className={styles.currencyBreakdownRow}>
                      <span className={styles.currencyBreakdownCode}>{currency}</span>
                      <span className={styles.currencyBreakdownAmount}>{formattedAmount}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </ActionModal>
        </Modal>
      )}

      {applyData && (
        <Modal open={modalState.isSuccess} onClose={handleCloseSuccessModal} aria-labelledby="add-bulk-wallet-success">
          <AcknowledgeModal
            result="positive"
            title={t('dialog.employees.bulkWallet.success.title')}
            description={t('dialog.employees.bulkWallet.success.description')}
            buttonText={t('button.close')}
            handleClose={handleCloseSuccessModal}
            buttonTestId="success-modal-btn"
          />
        </Modal>
      )}

      <Modal open={modalState.isError} onClose={handleCloseErrorModal} aria-labelledby="add-bulk-wallet-error">
        <AcknowledgeModal
          result="negative"
          title={t('dialog.employees.bulkWallet.error.title')}
          description={t('dialog.employees.bulkWallet.error.description')}
          buttonText={t('button.close')}
          handleClose={handleCloseErrorModal}
          buttonTestId="error-modal-btn"
        />
      </Modal>
    </div>
  )
}
